import React, { useState, useContext, memo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Button,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import { format, parseISO, isSameDay } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import TransferTicketModalCustom from '../../components/TransferTicketModalCustom';
import canTransferTicket from '../../helpers/canTransferTicket';
import TicketConversationModal from '../../components/TicketConversationModal';
import KanbanTriggerFlowAction from '../../components/KanbanTriggerFlowAction';
import toastError from '../../errors/toastError';
import { i18n } from '../../translate/i18n';
import { getBackendUrl } from '../../config';

const useStyles = makeStyles(theme => ({
  card: {
    padding: '10px 12px',
    backgroundColor:
      theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff',
    borderRadius: 10,
    border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
    boxShadow: theme.palette.type === 'dark'
      ? '0 1px 3px rgba(0,0,0,0.3)'
      : '0 1px 3px rgba(0,0,0,0.06)',
    marginBottom: 6,
    cursor: 'grab',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    borderLeft: '3px solid transparent',
    '&:hover': {
      boxShadow: theme.palette.type === 'dark'
        ? '0 4px 12px rgba(0,0,0,0.4)'
        : '0 4px 12px rgba(0,0,0,0.1)',
      borderColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
      '& $actions': {
        opacity: 1,
        backgroundColor:
          theme.palette.type === 'dark'
            ? 'rgba(0, 0, 0, 0.35)'
            : '#ffffff',
      },
    },
  },
  cardUnread: {
    backgroundColor:
      theme.palette.type === 'dark' ? '#243328' : '#f3faf4',
  },
  // ── Header: avatar + nome + horário ──
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 6,
  },
  avatarWrapper: {
    position: 'relative',
    flexShrink: 0,
  },
  avatar: {
    width: 36,
    height: 36,
    fontSize: '0.85rem',
  },
  unreadDot: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#4CAF50',
    border: `2px solid ${theme.palette.type === 'dark' ? theme.palette.background.paper : '#fff'}`,
  },
  nameBlock: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  metaLine: {
    fontSize: '0.68rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  timeStamp: {
    fontSize: '0.68rem',
    color: theme.palette.text.secondary,
    flexShrink: 0,
    alignSelf: 'flex-start',
    marginTop: 2,
  },
  timeStampUnread: {
    fontSize: '0.68rem',
    color: '#4CAF50',
    fontWeight: 600,
    flexShrink: 0,
    alignSelf: 'flex-start',
    marginTop: 2,
  },
  // ── Body: última mensagem ──
  lastMessage: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    marginBottom: 8,
    wordBreak: 'break-word',
  },
  // ── Footer: tags + ações ──
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 24,
  },
  tags: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  connectionDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  connectionName: {
    fontSize: '0.62rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 60,
  },
  valueBadge: {
    fontSize: '0.65rem',
    fontWeight: 600,
    color: '#2E7D32',
    backgroundColor: 'rgba(76,175,80,0.1)',
    padding: '1px 6px',
    borderRadius: 4,
    whiteSpace: 'nowrap',
  },
  ticketId: {
    fontSize: '0.6rem',
    color: theme.palette.text.disabled,
    whiteSpace: 'nowrap',
  },
  // ── Actions: aparecem no hover ──
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    opacity: 0.92,
    flexShrink: 0,
    padding: '2px 4px',
    borderRadius: 6,
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(0, 0, 0, 0.25)'
        : 'rgba(255, 255, 255, 0.92)',
  },
  actionBtn: {
    padding: 3,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  actionBtnMoney: {
    padding: 3,
    color: '#4CAF50',
    '&:hover': {
      backgroundColor: 'rgba(76,175,80,0.08)',
    },
  },
  actionBtnClose: {
    padding: 3,
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: 'rgba(244,67,54,0.08)',
    },
  },
  closedBadge: {
    fontSize: '0.6rem',
    color: theme.palette.text.disabled,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  // ── Dialog ──
  dialogPaper: {
    borderRadius: 12,
  },
  dialogButton: {
    borderRadius: 8,
    textTransform: 'none',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },
  },
}));

const KanbanCard = ({ ticket, index, updateTicket, onTicketClosed, onTicketTransferred }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [conversationModalOpen, setConversationModalOpen] = useState(false);

  const handleCardClick = () => {
    history.push(`/tickets/${ticket.uuid}`);
  };

  const hasUnread = Number(ticket.unreadMessages) > 0;
  const isClosed = ticket.status === 'closed';
  const canViewContent = user?.profile === 'admin' || ticket.userId === user?.id;
  const customFields = ticket.contact.extraInfo || [];
  const valueFieldIndex = customFields.findIndex(field => field.name === 'valor');
  const valueField = valueFieldIndex !== -1 ? customFields[valueFieldIndex] : null;
  const opportunityValue = valueField ? parseFloat(valueField.value) : null;
  const hasOpportunityValueFilled =
    Boolean(valueField) &&
    String(valueField.value ?? '').trim() !== '' &&
    Number.isFinite(opportunityValue);

  const buildAvatarSrc = (rawUrlPicture) => {
    if (!rawUrlPicture || typeof rawUrlPicture !== 'string') return null;
    const normalized = rawUrlPicture.trim();
    if (!normalized) return null;
    if (/^https?:\/\//i.test(normalized)) return normalized;
    const backendUrl = getBackendUrl();
    if (!backendUrl) return null;
    if (normalized.startsWith('/')) return `${backendUrl}${normalized}`;
    return `${backendUrl}/public/${normalized}`;
  };

  const avatarSrc = buildAvatarSrc(ticket?.contact?.urlPicture);

  const handleAvatarError = (event) => {
    const img = event?.currentTarget;
    if (!img) return;
    img.onerror = null;
    img.src = '/nopicture.png';
  };

  const handleOpenModal = () => {
    setNewValue(valueField ? valueField.value.toString() : '');
    setOpen(true);
  };

  const handleCloseModal = () => setOpen(false);

  const fetchContactExtraInfo = async (contactId) => {
    const { data } = await api.get(`/contacts/${contactId}`);
    return Array.isArray(data?.extraInfo) ? data.extraInfo : [];
  };

  const updateContactValue = async (contactId, value) => {
    try {
      const currentExtraInfo = await fetchContactExtraInfo(contactId);
      const preservedExtraInfo = currentExtraInfo.filter(field => field?.name !== 'valor');
      await api.put(`/contacts/${contactId}`, {
        extraInfo: [...preservedExtraInfo, { name: 'valor', value: value.toString() }],
      });
    } catch (error) {
      console.error('Erro ao atualizar o valor:', error);
    }
  };

  const removeContactValue = async () => {
    try {
      const currentExtraInfo = await fetchContactExtraInfo(ticket.contact.id);
      const preservedExtraInfo = currentExtraInfo.filter(field => field?.name !== 'valor');
      await api.put(`/contacts/${ticket.contact.id}`, { extraInfo: preservedExtraInfo });
      if (valueFieldIndex !== -1) customFields.splice(valueFieldIndex, 1);
      updateTicket({ ...ticket });
    } catch (error) {
      console.error('Erro ao remover o valor:', error);
    }
  };

  const handleSaveValue = async () => {
    await updateContactValue(ticket.contact.id, newValue);
    if (valueField) {
      valueField.value = newValue;
    } else {
      if (!ticket.contact.extraInfo) ticket.contact.extraInfo = [];
      ticket.contact.extraInfo.push({ name: 'valor', value: newValue });
    }
    updateTicket({ ...ticket });
    setOpen(false);
  };

  const handleCloseTicket = async (e) => {
    e.stopPropagation();
    if (closing) return;
    setClosing(true);
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: 'closed',
        userId: user?.id || null,
        queueId: ticket?.queue?.id || ticket?.queueId || null,
        sendFarewellMessage: false,
      });
      if (typeof onTicketClosed === 'function') onTicketClosed(ticket.id);
    } catch (err) {
      toastError(err);
    } finally {
      setClosing(false);
    }
  };

  const handleOpenTransfer = (e) => {
    e.stopPropagation();
    setTransferModalOpen(true);
  };

  const handleOpenConversationModal = (e) => {
    e.stopPropagation();
    setConversationModalOpen(true);
  };

  // ── Formatação ──
  const formattedTime = (() => {
    try {
      const date = parseISO(ticket.updatedAt);
      return isSameDay(date, new Date())
        ? format(date, 'HH:mm')
        : format(date, 'dd/MM');
    } catch { return ''; }
  })();

  const connectionColor = ticket.whatsapp?.color || '#4CAF50';
  const cardSurfaceBg =
    theme.palette.type === 'dark'
      ? hasUnread
        ? '#243328'
        : '#2a2a2a'
      : hasUnread
        ? '#f3faf4'
        : '#ffffff';

  return (
    <Draggable draggableId={ticket.id.toString()} index={index}>
      {(provided) => (
        <div
          className={`${classes.card} ${hasUnread ? classes.cardUnread : ''}`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            borderLeftColor: connectionColor,
            backgroundColor: cardSurfaceBg,
          }}
        >
          {/* ── Header ── */}
          <div className={classes.header}>
            <div className={classes.avatarWrapper}>
              <Avatar
                src={avatarSrc || '/nopicture.png'}
                className={classes.avatar}
                imgProps={{ onError: handleAvatarError, loading: 'lazy' }}
              >
                {ticket.contact.name?.[0]?.toUpperCase()}
              </Avatar>
              {hasUnread && <span className={classes.unreadDot} />}
            </div>

            <div className={classes.nameBlock}>
              <Typography className={classes.contactName}>
                {ticket.contact.name || ' '}
              </Typography>
              <Typography className={classes.metaLine}>
                {ticket.user ? `${ticket.user.name}` : ''}
                {ticket.user && ticket.id ? ' · ' : ''}
                #{ticket.id}
              </Typography>
            </div>

            <Typography className={hasUnread ? classes.timeStampUnread : classes.timeStamp}>
              {formattedTime}
            </Typography>
          </div>

          {/* ── Última mensagem ── */}
          {ticket.lastMessage && (
            <Typography className={classes.lastMessage}>
              {ticket.lastMessage}
            </Typography>
          )}

          {/* ── Footer ── */}
          <div className={classes.footer}>
            <div className={classes.tags}>
              {ticket.whatsapp && (
                <Tooltip title={ticket.whatsapp.name} placement="top">
                  <span
                    className={classes.connectionDot}
                    style={{ backgroundColor: connectionColor }}
                  />
                </Tooltip>
              )}
              {ticket.whatsapp && (
                <Typography className={classes.connectionName}>
                  {ticket.whatsapp.name}
                </Typography>
              )}
              {hasOpportunityValueFilled && (
                <span className={classes.valueBadge}>
                  R$ {opportunityValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              )}
              {isClosed && (
                <span className={classes.closedBadge}>
                  <CheckCircleIcon style={{ fontSize: 12 }} />
                  Fechado
                </span>
              )}
            </div>

            {/* Ações — visíveis no hover */}
            <div className={classes.actions}>
              <Tooltip title="Conversa" placement="top">
                <IconButton size="small" className={classes.actionBtn} onClick={handleCardClick}>
                  <ChatBubbleOutlineIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {canViewContent && (
                <Tooltip title="Espiar" placement="top">
                  <IconButton size="small" className={classes.actionBtn} onClick={handleOpenConversationModal}>
                    <VisibilityOutlinedIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <KanbanTriggerFlowAction ticket={ticket} className={classes.actionBtn} />
              <Tooltip title={hasOpportunityValueFilled ? `R$ ${opportunityValue.toFixed(2)}` : 'Valor'} placement="top">
                <IconButton
                  size="small"
                  className={hasOpportunityValueFilled ? classes.actionBtnMoney : classes.actionBtn}
                  onClick={(e) => { e.stopPropagation(); handleOpenModal(); }}
                >
                  <AttachMoneyIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {hasOpportunityValueFilled && (
                <Tooltip title="Remover valor" placement="top">
                  <IconButton
                    size="small"
                    className={classes.actionBtnClose}
                    onClick={(e) => { e.stopPropagation(); removeContactValue(); }}
                  >
                    <HighlightOffIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              {!isClosed && (
                <>
                  {canTransferTicket(user) && (
                    <Tooltip title="Transferir" placement="top">
                      <IconButton size="small" className={classes.actionBtn} onClick={handleOpenTransfer}>
                        <SwapHorizIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Fechar" placement="top">
                    <IconButton size="small" className={classes.actionBtnClose} onClick={handleCloseTicket} disabled={closing}>
                      <CloseIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </div>
          </div>

          {/* ── Dialogs ── */}
          <Dialog open={open} onClose={handleCloseModal} classes={{ paper: classes.dialogPaper }}>
            <DialogTitle>{valueField ? 'Editar' : 'Atribuir'} Valor da Oportunidade</DialogTitle>
            <DialogContent>
              <TextField
                label="Valor"
                type="number"
                fullWidth
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                variant="outlined"
                size="small"
                className={classes.textField}
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal} color="secondary" variant="outlined" className={classes.dialogButton}>
                Cancelar
              </Button>
              <Button onClick={handleSaveValue} color="primary" variant="contained" className={classes.dialogButton}>
                Salvar
              </Button>
            </DialogActions>
          </Dialog>

          {canTransferTicket(user) && transferModalOpen && (
            <TransferTicketModalCustom
              modalOpen={transferModalOpen}
              onClose={() => setTransferModalOpen(false)}
              ticketid={ticket.id}
              ticket={ticket}
              onSuccess={() => onTicketTransferred && onTicketTransferred(ticket.id)}
            />
          )}

          {conversationModalOpen && (
            <TicketConversationModal
              open={conversationModalOpen}
              onClose={() => setConversationModalOpen(false)}
              ticket={ticket}
            />
          )}
        </div>
      )}
    </Draggable>
  );
};

const areKanbanCardPropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.ticket === nextProps.ticket &&
    prevProps.index === nextProps.index &&
    prevProps.updateTicket === nextProps.updateTicket &&
    prevProps.onTicketClosed === nextProps.onTicketClosed &&
    prevProps.onTicketTransferred === nextProps.onTicketTransferred
  );
};

export default memo(KanbanCard, areKanbanCardPropsEqual);
